import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Row, Col, Button, TabContent, TabPane } from 'reactstrap';
import { animateScroll } from 'react-scroll';

import SummaryModal from '../Vendor/Modal';
import BZAPIService from '../../../services/BZAPIService';
import ProductPriority from '../section/ProductPriority';
import TabNav from '../section/TabNav';
import TabOne from '../section/TabOne';
import DefaultConfigTab from '../section/DefaultConfigTab';
import ExecutionStatsTab from '../section/ExecutionStatsTab';
import AITab from '../section/AITab';
import { useScheduling } from '../../../hooks/useScheduling';

// constants
import SETTINGS from '../../../constants/settings';

const ConfigFormVmwareVCenter = ({
  vendor,
  isReadOnly,
  vendorLabel,
  vendorId,
  bugsDateBack,
  setBugsDateBack,
  snCompanySysId,
  setSnCompanySysId,
  vendorPriorityOptions,
  vendorStatusOptions,
  snIntegrationCheck,
  services,
  vendors,
  priorityStats,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  setVendorStatuses,
  vendorStatuses,
  fields,
  handleFieldChange,
  priorityMaps,
  setPriorityMaps,
  snPriorityOptions,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  setServices,
  setVendors,
  buildVendorStat,
  setPriorityStats,
  setBugs,
  isConfigured,
  syncOnly,
  setSyncOnly,
  loading,
  isServiceActive,
  setIsServiceActive,
  allRequired,
  setAllRequired,
  isUpdated,
  setIsUpdated
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('1');
  const [modalOpen, setModalOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [isForceRun, setIsForceRun] = useState(false);

  // Use scheduling hook for state and save function
  const {
    timeUnit,
    setTimeUnit,
    timeValue,
    setTimeValue,
    schedulingMode,
    setSchedulingMode,
    schedules,
    setSchedules,
    nextRun,
    setNextRun,
    saveBugServiceFrequency
  } = useScheduling();

  async function setBugServiceFrequency() {
    await saveBugServiceFrequency(vendorId);
  }

  const navigate = useNavigate();

  useEffect(() => {
    setIsUpdated(true);
    if (
      bugsDateBack?.length &&
      snCompanySysId?.value &&
      priorityMaps?.length &&
      vendorStatuses.length &&
      !priorityMaps.some(mapping => !mapping.snPriority || !mapping.vendorPriority)
    ) {
      setAllRequired(true);
    } else {
      setAllRequired(false);
    }
  }, [
    fields,
    bugsDateBack,
    vendorStatuses,
    priorityMaps,
    keywords,
    searchFields,
    snCompanySysId,
    setAllRequired,
    setIsUpdated
  ]);

  async function saveConfig() {
    const vendorPriorities = priorityMaps.map(mapping => {
      return {
        value: mapping.vendorPriority.value,
        label: mapping.vendorPriority.value,
        snPriority: mapping.snPriority.value
      };
    });

    const data = {
      bugsDateBack: bugsDateBack,
      snAffectedCIQuery: fields.snAffectedCIQuery,
      vendorPriorities,
      vendorStatuses,
      snCompanySysId: snCompanySysId.value ? snCompanySysId.value : fields.snCompanySysId,
      type: SETTINGS.VENDOR,
      excludedKws: keywords,
      searchFields: searchFields
    };

    return BZAPIService.saveSetting(vendorId, syncOnly, data, isForceRun);
  }

  async function handleSave() {
    try {
      const filteredPriorities = priorityMaps.filter(mapping => {
        return mapping?.vendorPriority?.value && mapping?.snPriority?.value;
      });
      setPriorityMaps(filteredPriorities);

      setIsLoading(true);
      setIsSubmitted(true);
      // Validate
      if (!bugsDateBack.length || !snCompanySysId) {
        setValidated(false);
        setActiveTab('3');
        setIsLoading(false);
        return;
      }

      if (!vendorStatuses.length || !priorityMaps.length) {
        setIsLoading(false);
        setValidated(false);
        setActiveTab('4');
        return;
      }

      await saveConfig();
      setBugServiceFrequency();
      setIsLoading(false);
      setIsSubmitted(false);
      setModalOpen(false);
      setIsForceRun(false);
      setActiveTab('1');
      toast.success(`Settings Saved!`);
    } catch (e) {
      setIsLoading(false);
      toast.error('Failed to save.');
      console.log('e', e);
    }
  }

  function handleValidationError() {
    setModalOpen(!modalOpen);
    animateScroll.scrollTo(70);
  }

  return (
    <>
      <TabNav
        vendorId={vendorId}
        credentials={false}
        activeTab={activeTab}
        snIntegrationCheck={snIntegrationCheck}
        setActiveTab={setActiveTab}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        setServices={setServices}
        setVendors={setVendors}
        buildVendorStat={buildVendorStat}
        setPriorityStats={setPriorityStats}
        setBugs={setBugs}
        isConfigured={isConfigured}
        hasBugRisk={false}
        loading={loading}
        aiSupported={vendor?.aiSupported}
      />

      <TabContent>
        <TabOne
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          snIntegrationCheck={snIntegrationCheck}
          services={services}
          vendors={vendors}
          priorityStats={priorityStats}
          isConfigured={isConfigured}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          setIsForceRun={setIsForceRun}
          timeUnit={timeUnit}
          setTimeUnit={setTimeUnit}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          nextRun={nextRun}
          setNextRun={setNextRun}
          schedulingMode={schedulingMode}
          setSchedulingMode={setSchedulingMode}
          schedules={schedules}
          setSchedules={setSchedules}
          loading={loading}
          isServiceActive={isServiceActive}
          setIsServiceActive={setIsServiceActive}
        />
        <DefaultConfigTab
          vendor={vendor}
          activeTab={activeTab}
          vendorLabel={vendorLabel}
          bugsDateBack={bugsDateBack}
          setBugsDateBack={setBugsDateBack}
          isSubmitted={isSubmitted}
          snCompanySysIdErrors={snCompanySysIdErrors}
          snCompanySysIdOptions={snCompanySysIdOptions}
          setSnCompanySysId={setSnCompanySysId}
          snCompanySysId={snCompanySysId}
          fields={fields}
          handleFieldChange={handleFieldChange}
        />
        <form noValidate style={{ minWidth: '100%' }}>
          <TabPane tabId="8">{activeTab === '8' && <AITab vendorId={vendorId} />}</TabPane>
          <TabPane tabId="4">
            {activeTab === '4' && (
              <ProductPriority
                title="Default Bug Configuration"
                vendorPriorityOptions={vendorPriorityOptions}
                vendorStatusOptions={vendorStatusOptions}
                setVendorStatuses={setVendorStatuses}
                vendorStatuses={vendorStatuses}
                priorityMaps={priorityMaps}
                setPriorityMaps={setPriorityMaps}
                snPriorityOptions={snPriorityOptions}
                keywords={keywords}
                setKeywords={setKeywords}
                searchFields={searchFields}
                setSearchFields={setSearchFields}
                vendorId={vendorId}
                vendorLabel={vendorLabel}
              />
            )}
          </TabPane>
          <TabPane tabId="6">{activeTab === '6' && <ExecutionStatsTab vendorId={vendorId} />}</TabPane>

          <Row className={`${activeTab === '1' ? 'd-none' : ''} flex-between-center`}>
            <Col xs="auto">
              <Button
                block
                color="falcon-danger"
                onClick={() => {
                  console.log(`/vendors/${vendorId}/configuration`);
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              <SummaryModal
                modalOpen={modalOpen}
                isReadOnly={isReadOnly}
                setModalOpen={setModalOpen}
                handleSave={handleSave}
                isLoading={isLoading}
                vendorLabel={vendorLabel}
                vendorId={vendorId}
                validated={validated}
                setValidated={setValidated}
                syncOnly={syncOnly}
                setSyncOnly={setSyncOnly}
                handleValidationError={handleValidationError}
                allRequired={allRequired}
                isUpdated={isUpdated}
              />
            </Col>
          </Row>
        </form>
      </TabContent>
    </>
  );
};
ConfigFormVmwareVCenter.propTypes = {
  vendor: PropTypes.object,
  isReadOnly: PropTypes.bool,
  vendorLabel: PropTypes.string,
  vendorId: PropTypes.string,
  vendorPriorityOptions: PropTypes.array,
  vendorStatusOptions: PropTypes.array,
  snCompanySysIdOptions: PropTypes.object,
  snCompanySysId: PropTypes.object,
  snIntegrationCheck: PropTypes.bool,
  services: PropTypes.array,
  vendors: PropTypes.array,
  priorityStats: PropTypes.object,
  setPriorityStats: PropTypes.func,
  snCompanySysIdErrors: PropTypes.bool,
  setVendorStatuses: PropTypes.func,
  vendorStatuses: PropTypes.array,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  snPriorityOptions: PropTypes.array,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  setSnCompanySysId: PropTypes.func,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  setServices: PropTypes.func,
  setVendors: PropTypes.func,
  buildVendorStat: PropTypes.func,
  setBugs: PropTypes.func,
  isConfigured: PropTypes.bool,
  syncOnly: PropTypes.bool,
  setSyncOnly: PropTypes.func,
  loading: PropTypes.bool,
  autoClose: PropTypes.bool,
  setAutoClose: PropTypes.func,
  setIsServiceActive: PropTypes.func,
  isServiceActive: PropTypes.number,
  allRequired: PropTypes.bool,
  isUpdated: PropTypes.bool,
  setAllRequired: PropTypes.func,
  setIsUpdated: PropTypes.func
};
export default ConfigFormVmwareVCenter;
